import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { 
  ArrowRight,
  AlertCircle,
  GripVertical,
  Pencil,
  X,
  Check,
  Eye
} from 'lucide-react';

// Import dashboard widget components
import {
  UpcomingMatchesWidget,
  UpcomingTrainingWidget,
  RecentFormWidget,
  RecentResultsWidget,
  CupProgressWidget,
  TopScorersWidget,
  TopAssistersWidget,
  TrainingAttendanceWidget,
  RecordSummaryWidget,
  QuickStatsWidget,
  NextFixtureCountdownWidget,
  FormOfTheWeekWidget,
  WidgetWrapper,
  DEFAULT_WIDGET_ORDER,
  mergeWidgetOrder
} from '../components/dashboard';
import WelfareReminderBanner from '../components/welfare/WelfareReminderBanner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Dashboard() {
  const { user, updateUser, isAdmin } = useAuth();
  const [stats, setStats] = useState(null);
  const [trainingAttendance, setTrainingAttendance] = useState([]);
  const [upcomingTrainings, setUpcomingTrainings] = useState([]);
  const [cupProgress, setCupProgress] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [widgetOrder, setWidgetOrder] = useState(mergeWidgetOrder(user?.dashboard_widget_order, DEFAULT_WIDGET_ORDER));
  const [hiddenWidgets, setHiddenWidgets] = useState((user?.dashboard_hidden_widgets || []).filter(id => DEFAULT_WIDGET_ORDER.includes(id)));
  const [originalOrder, setOriginalOrder] = useState([]);
  const [originalHidden, setOriginalHidden] = useState([]);
  const [draggedWidget, setDraggedWidget] = useState(null);
  const [dragOverWidget, setDragOverWidget] = useState(null);
  
  // AI Headlines state
  const [aiHeadlines, setAiHeadlines] = useState([]);
  // Opponent badge lookup
  const [opponentBadgeMap, setOpponentBadgeMap] = useState({});
  const showOpponentBadges = user?.show_opponent_badges !== false;

  useEffect(() => {
    if (user?.current_season_id) {
      fetchStats();
      fetchTrainingAttendance();
      fetchUpcomingTrainings();
      fetchCupProgress();
      
      // Fetch AI headlines if ticker is enabled and mode is AI
      if (user?.ticker_enabled && user?.ticker_mode === 'ai') {
        fetchAiHeadlines();
      }
    } else {
      setLoading(false);
    }
  }, [user?.current_season_id, user?.ticker_enabled, user?.ticker_mode]);

  useEffect(() => {
    if (!showOpponentBadges) {
      setOpponentBadgeMap({});
      return;
    }
    const fetchOpponents = async () => {
      try {
        const res = await axios.get(`${API}/opponents`);
        const map = {};
        (res.data || []).forEach((o) => {
          if (o?.name) map[o.name.toLowerCase().trim()] = o.badge || null;
        });
        setOpponentBadgeMap(map);
      } catch (error) {
        // Silent fail
      }
    };
    fetchOpponents();
  }, [showOpponentBadges]);

  const fetchAiHeadlines = async () => {
    try {
      const response = await axios.get(`${API}/ai-headlines`);
      setAiHeadlines(response.data.headlines || []);
    } catch (error) {
      console.error('Failed to fetch AI headlines:', error);
      setAiHeadlines(['Team news coming soon!']);
    }
  };

  useEffect(() => {
    if (user?.dashboard_widget_order) {
      setWidgetOrder(mergeWidgetOrder(user.dashboard_widget_order, DEFAULT_WIDGET_ORDER));
    }
    if (user?.dashboard_hidden_widgets) {
      // Drop any legacy IDs that no longer exist in DEFAULT_WIDGET_ORDER so widgets aren't mistakenly hidden
      setHiddenWidgets(user.dashboard_hidden_widgets.filter(id => DEFAULT_WIDGET_ORDER.includes(id)));
    }
  }, [user?.dashboard_widget_order, user?.dashboard_hidden_widgets]);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/dashboard/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTrainingAttendance = async () => {
    try {
      const response = await axios.get(`${API}/training-attendance`);
      setTrainingAttendance(response.data);
    } catch (error) {
      console.error('Failed to fetch training attendance:', error);
    }
  };

  const fetchUpcomingTrainings = async () => {
    try {
      const response = await axios.get(`${API}/trainings`);
      const today = new Date().toISOString().split('T')[0];
      const upcoming = response.data
        .filter(t => t.date >= today)
        .sort((a, b) => a.date.localeCompare(b.date) || (a.start_time || '').localeCompare(b.start_time || ''))
        .slice(0, 3);
      setUpcomingTrainings(upcoming);
    } catch (error) {
      console.error('Failed to fetch upcoming trainings:', error);
    }
  };

  const fetchCupProgress = async () => {
    try {
      const response = await axios.get(`${API}/cup-progress`);
      setCupProgress(response.data);
    } catch (error) {
      console.error('Failed to fetch cup progress:', error);
    }
  };

  const handleEditMode = () => {
    setOriginalOrder([...widgetOrder]);
    setOriginalHidden([...hiddenWidgets]);
    setEditMode(true);
  };

  const handleCancelEdit = () => {
    setWidgetOrder(originalOrder);
    setHiddenWidgets(originalHidden);
    setEditMode(false);
    setDraggedWidget(null);
    setDragOverWidget(null);
  };

  const handleSaveOrder = async () => {
    try {
      const response = await axios.put(`${API}/settings`, {
        dashboard_widget_order: widgetOrder,
        dashboard_hidden_widgets: hiddenWidgets
      });
      updateUser(response.data);
      toast.success('Dashboard layout saved');
      setEditMode(false);
    } catch (error) {
      toast.error('Failed to save layout');
    }
  };

  const toggleWidgetVisibility = (widgetId) => {
    if (hiddenWidgets.includes(widgetId)) {
      setHiddenWidgets(hiddenWidgets.filter(id => id !== widgetId));
    } else {
      setHiddenWidgets([...hiddenWidgets, widgetId]);
    }
  };

  const handleDragStart = (e, widgetId) => {
    setDraggedWidget(widgetId);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, widgetId) => {
    e.preventDefault();
    if (widgetId !== draggedWidget) {
      setDragOverWidget(widgetId);
    }
  };

  const handleDragLeave = () => {
    setDragOverWidget(null);
  };

  const handleDrop = (e, targetWidgetId) => {
    e.preventDefault();
    if (draggedWidget && draggedWidget !== targetWidgetId) {
      const newOrder = [...widgetOrder];
      const draggedIndex = newOrder.indexOf(draggedWidget);
      const targetIndex = newOrder.indexOf(targetWidgetId);
      
      newOrder.splice(draggedIndex, 1);
      newOrder.splice(targetIndex, 0, draggedWidget);
      
      setWidgetOrder(newOrder);
    }
    setDraggedWidget(null);
    setDragOverWidget(null);
  };

  const handleDragEnd = () => {
    setDraggedWidget(null);
    setDragOverWidget(null);
  };

  // Widget map with components and their grid classes
  const widgetMap = {
    'next-fixture-countdown': {
      component: () => <NextFixtureCountdownWidget stats={stats} user={user} opponentBadgeMap={opponentBadgeMap} />,
      gridClass: ''
    },
    'upcoming-matches': { 
      component: () => <UpcomingMatchesWidget stats={stats} user={user} opponentBadgeMap={opponentBadgeMap} />, 
      gridClass: '' 
    },
    'recent-form': { 
      component: () => <RecentFormWidget stats={stats} user={user} />, 
      gridClass: 'col-span-full' 
    },
    'recent-results': { 
      component: () => <RecentResultsWidget stats={stats} user={user} opponentBadgeMap={opponentBadgeMap} />, 
      gridClass: '' 
    },
    'cup-progress': { 
      component: () => <CupProgressWidget cupProgress={cupProgress} user={user} />, 
      gridClass: '' 
    },
    'upcoming-training': { 
      component: () => <UpcomingTrainingWidget upcomingTrainings={upcomingTrainings} user={user} />, 
      gridClass: '' 
    },
    'top-scorers': { 
      component: () => <TopScorersWidget stats={stats} user={user} />, 
      gridClass: '' 
    },
    'top-assisters': { 
      component: () => <TopAssistersWidget stats={stats} user={user} />, 
      gridClass: '' 
    },
    'form-of-the-week': {
      component: () => <FormOfTheWeekWidget user={user} />,
      gridClass: ''
    },
    'training-attendance': { 
      component: () => <TrainingAttendanceWidget trainingAttendance={trainingAttendance} user={user} />, 
      gridClass: '' 
    },
    'record-summary': { 
      component: () => <RecordSummaryWidget stats={stats} />, 
      gridClass: 'col-span-full' 
    },
    'quick-stats': { 
      component: () => <QuickStatsWidget stats={stats} />, 
      gridClass: 'col-span-full' 
    }
  };

  // Render widgets in order
  const renderWidgets = () => {
    return widgetOrder.map((widgetId) => {
      const widget = widgetMap[widgetId];
      if (!widget) return null;
      
      // In view mode, don't render hidden widgets
      if (!editMode && hiddenWidgets.includes(widgetId)) {
        return null;
      }
      
      const WidgetComponent = widget.component;
      return (
        <WidgetWrapper 
          key={widgetId} 
          id={widgetId} 
          className={widget.gridClass}
          editMode={editMode}
          draggedWidget={draggedWidget}
          dragOverWidget={dragOverWidget}
          hiddenWidgets={hiddenWidgets}
          onDragStart={handleDragStart}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onDragEnd={handleDragEnd}
          onToggleVisibility={toggleWidgetVisibility}
        >
          <WidgetComponent />
        </WidgetWrapper>
      );
    });
  };

  if (!user?.current_season_id) {
    return (
      <Layout>
        <div className="flex flex-col items-center justify-center min-h-[60vh] text-center" data-testid="no-season-message">
          <div className="w-20 h-20 rounded-2xl bg-amber-100 flex items-center justify-center mb-6">
            <AlertCircle className="w-10 h-10 text-amber-600" strokeWidth={1.5} />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-3 font-title">No Season Selected</h2>
          <p className="text-gray-500 mb-6 max-w-md">
            Create a season to start tracking your team's performance, players, and matches.
          </p>
          <Link to="/seasons">
            <Button className="btn-primary rounded-xl px-6" data-testid="create-season-btn">
              Create Season
              <ArrowRight className="w-4 h-4 ml-2" strokeWidth={1.5} />
            </Button>
          </Link>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      {/* Header Image - Full width, no padding, flush to top */}
      {user?.header_image && (
        <div className="-mx-4 sm:-mx-6 lg:-mx-8 -mt-4 sm:-mt-6 lg:-mt-8 mb-6 overflow-hidden relative">
          <img 
            src={user.header_image.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${user.header_image}` : user.header_image} 
            alt="Team Header" 
            className="w-full h-48 sm:h-60 md:h-72 lg:h-80 object-cover object-top"
            data-testid="clubhouse-header-image"
          />
          {/* Gradient overlay */}
          <div 
            className="absolute inset-0 pointer-events-none"
            style={{
              background: 'linear-gradient(to top, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.5) 35%, rgba(0,0,0,0.2) 70%, transparent 100%)'
            }}
          />
          {/* Breaking News Ticker */}
          {user?.ticker_enabled && (
            <div 
              className="absolute bottom-4 left-4 right-4 sm:bottom-6 sm:left-6 sm:right-6 lg:left-[25px] lg:right-8"
              data-testid="breaking-news-ticker"
            >
              {/* Manual Mode */}
              {user?.ticker_mode !== 'ai' && user?.ticker_text && (
                <div className="flex items-center gap-3 text-white">
                  <span className="font-bold text-sm sm:text-base uppercase whitespace-nowrap shrink-0 bg-black/30 px-2 py-1 rounded">
                    {user?.ticker_label || 'BREAKING NEWS:'}
                  </span>
                  <div className="ticker-container flex-1">
                    <div className="ticker-track">
                      {[0, 1].map((copy) => (
                        <span key={copy} className="ticker-item text-sm sm:text-base">
                          {user?.ticker_text}
                          <span className="ticker-separator text-xs grayscale">⚽</span>
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              )}
              
              {/* AI Mode */}
              {user?.ticker_mode === 'ai' && aiHeadlines.length > 0 && (
                <div className="flex items-center gap-3 text-white">
                  <span className="font-bold text-sm sm:text-base uppercase whitespace-nowrap shrink-0 bg-black/30 px-2 py-1 rounded">
                    {user?.ticker_label || 'HEADLINES:'}
                  </span>
                  <div className="ticker-container flex-1">
                    <div className="ticker-track">
                      {[0, 1].map((copy) => (
                        <span key={copy} className="inline-flex">
                          {aiHeadlines.map((headline, index) => (
                            <span key={`${copy}-${index}`} className="ticker-item text-sm sm:text-base">
                              {headline}
                              <span className="ticker-separator text-xs grayscale">⚽</span>
                            </span>
                          ))}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
      
      <div className="space-y-6 page-slide-in" data-testid="dashboard-container">
        {/* Welfare reminder (admin only) */}
        {isAdmin && <WelfareReminderBanner />}

        {/* Header */}
        <div className="flex items-center justify-between gap-4">
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 font-title tracking-tight">
            Clubhouse
          </h1>
          
          {/* Edit Layout Button - Admin Only */}
          {isAdmin && !editMode && (
            <Button
              variant="outline"
              size="icon"
              onClick={handleEditMode}
              className="rounded-xl shrink-0 bg-gray-100 border-gray-200 text-gray-600 hover:bg-gray-200"
              data-testid="edit-layout-btn"
            >
              <Pencil className="w-4 h-4" strokeWidth={1.5} />
            </Button>
          )}
          {isAdmin && editMode && (
            <div className="flex gap-2 shrink-0">
              <Button
                variant="outline"
                size="icon"
                onClick={handleCancelEdit}
                className="rounded-xl"
                data-testid="cancel-layout-btn"
              >
                <X className="w-4 h-4" strokeWidth={1.5} />
              </Button>
              <Button
                size="icon"
                onClick={handleSaveOrder}
                className="rounded-xl"
                style={{ 
                  backgroundColor: user?.button_color || '#064E3B',
                  color: 'white'
                }}
                data-testid="save-layout-btn"
              >
                <Check className="w-4 h-4" strokeWidth={1.5} />
              </Button>
            </div>
          )}
        </div>

        {/* Edit Mode Banner */}
        {editMode && (
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-start gap-3">
            <GripVertical className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" strokeWidth={1.5} />
            <div className="text-blue-800 text-sm">
              <p className="font-semibold mb-1">Edit Mode</p>
              <ul className="space-y-1">
                <li className="flex items-center gap-2">
                  <GripVertical className="w-4 h-4" strokeWidth={1.5} />
                  <span>Drag widgets to reorder</span>
                </li>
                <li className="flex items-center gap-2">
                  <Eye className="w-4 h-4" strokeWidth={1.5} />
                  <span>Click eye icon to show/hide widgets</span>
                </li>
              </ul>
            </div>
          </div>
        )}

        {/* Widgets Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-[36px] md:gap-4">
          {renderWidgets()}
        </div>
      </div>
    </Layout>
  );
}
